/**
 * Heartbeat de présence — envoie un ping toutes les 30 s avec la page courante
 * et une éventuelle activité (ex: "L1MI • Math"). Détecte la page auto depuis
 * l'URL, et on peut préciser une activité via `setActivite`.
 *
 * À utiliser au niveau du layout principal (une seule instance).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { usePingMutation } from '../services/messagerieChat';

const HEARTBEAT_MS = 30_000;

// Map path → libellé humain
function libellePage(pathname: string): { page: string; libelle: string } {
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length === 0) return { page: 'accueil', libelle: 'Accueil' };
  const p = parts[0].toLowerCase();
  const carte: Record<string, string> = {
    dashboard: 'Tableau de bord',
    etudiants: 'Étudiants',
    matieres: 'Matières',
    notes: 'Notes',
    'notes-devoirs': 'Notes devoirs',
    'notes-examens': 'Notes examens',
    'notes-rattrapage': 'Rattrapage',
    releves: 'Relevés',
    associations: 'Association copies',
    ocr: 'OCR',
    reclamations: 'Réclamations',
    rachat: 'Rachat',
    fraudes: 'Fraudes',
    fenetres: 'Fenêtres saisie',
    'fenetres-saisie': 'Fenêtres saisie',
    'emplois-temps': 'Emplois du temps',
    'gestion-options': 'Options',
    'brassage-examens': 'Brassage',
    audit: 'Audit',
    messagerie: 'Messagerie',
    portail: 'Portail étudiant',
    'portail-parent': 'Portail parent',
    formateur: 'Portail formateur',
    surveillant: 'Surveillance',
    'depot-sujets': 'Dépôt sujets',
    parametres: 'Paramètres',
  };
  return {
    page: p,
    libelle: carte[p] || p.charAt(0).toUpperCase() + p.slice(1),
  };
}

// Garde-fou singleton : deux composants montent le hook sans le savoir
// (ProtectedRoute + MessagerieFloating). On garantit qu'un seul timer tourne.
let _globalHeartbeatActive = false;

export function useHeartbeatPresence(enabled = true) {
  const location = useLocation();
  const [ping] = usePingMutation();
  const [activiteExtra, setActiviteExtra] = useState('');
  const timerRef = useRef<any>(null);
  const ownsGlobalRef = useRef(false);
  const errorStreakRef = useRef(0);

  const send = useCallback(async () => {
    if (!enabled) return;
    const { page, libelle } = libellePage(location.pathname);
    const activite = activiteExtra
      ? `${libelle} • ${activiteExtra}`
      : libelle;
    try {
      await ping({ page, activite }).unwrap();
      errorStreakRef.current = 0; // succès → reset backoff
    } catch {
      errorStreakRef.current = Math.min(errorStreakRef.current + 1, 5);
    }
  }, [enabled, location.pathname, activiteExtra, ping]);

  // Fixe l'intervalle : nominal 30s, doublé à chaque erreur consécutive
  // (30s → 60s → 120s → 240s → 480s max). Évite de marteler un serveur down.
  const scheduleNext = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const backoff = Math.pow(2, errorStreakRef.current);
    const delay = Math.min(HEARTBEAT_MS * backoff, 8 * 60_000);
    timerRef.current = setTimeout(async () => {
      await send();
      scheduleNext();
    }, delay);
  }, [send]);

  useEffect(() => {
    if (!enabled) return;
    // Un SEUL hook prend la main sur le heartbeat global. Les autres montages
    // partagent le ping (leurs deps sur activite/pathname continuent à
    // fonctionner mais ne posent pas de timer supplémentaire).
    if (_globalHeartbeatActive) return;
    _globalHeartbeatActive = true;
    ownsGlobalRef.current = true;
    send();
    scheduleNext();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (ownsGlobalRef.current) {
        _globalHeartbeatActive = false;
        ownsGlobalRef.current = false;
      }
    };
  }, [enabled, send, scheduleNext]);

  // Envoie un ping quand la page redevient visible — mais seulement si on
  // est le propriétaire du heartbeat (évite le doublement).
  useEffect(() => {
    if (!ownsGlobalRef.current) return;
    const onVisibility = () => {
      if (document.visibilityState === 'visible') send();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [send]);

  return {
    setActivite: setActiviteExtra,
    forceRefresh: send,
  };
}
